/**
 * F2 (docs/briefs/morning-2026-07-07/06-feature-opportunities.md, WP-C /
 * candidate #7) — pure path math for the lens-comparison spider chart
 * (LensComparisonRadar.tsx). Mirrors radarPath.ts's own "pure logic out of
 * JSX" idiom exactly (this app's house convention) and REUSES radarPath.ts's
 * axisPoints/gridPoints/ringPoints/toPolygonString directly rather than
 * duplicating the geometry — same visual language as the existing 6-axis fit
 * radar (RadarChart.tsx), same math, different axis SOURCE.
 *
 * THE HONEST MAPPING: TailorLensRankingComparison (apps/throughline/src/
 * lib/api.ts) carries exactly `modelOrder: string[]` and `lensOrder:
 * string[]` — two orderings of the SAME achievement set, no numeric weights
 * anywhere in the served shape. A spider chart needs a 0-100 value per axis,
 * so this module derives one the only honest way available: RANK POSITION.
 * The achievement ranked first in a list scores 100, the achievement ranked
 * last scores the lowest step, evenly spaced in between
 * (100 * (n - 1 - index) / (n - 1)). This is a real transform of REAL rank
 * data — never an invented axis, never a fabricated weight. If
 * modelOrder/lensOrder ever disagree on SET membership (should not happen —
 * both orders are drawn from the same matched achievement pool per
 * TailoringService's own spliceLeadAchievements contract), an achievement
 * missing from one list gets no plotted point on that polygon's axis rather
 * than a guessed value — NO INVENTION extended to the display layer.
 */

export interface LensComparisonAxis {
  /** The achievement's own text — doubles as both the axis label and the
   *  stable key (achievement text is what the server actually returns; no
   *  separate id exists on this shape). */
  key: string;
  /** Rank-derived value (0-100) in the DEFAULT (model) order. Undefined if
   *  this achievement did not appear in modelOrder — never fabricated. */
  modelValue: number | undefined;
  /** Rank-derived value (0-100) in the LENS-RANKED order. Undefined if this
   *  achievement did not appear in lensOrder — never fabricated. */
  lensValue: number | undefined;
}

/** Rank position -> 0-100 value, evenly spaced top-to-bottom. A single-item
 *  list has nothing to rank against another item, so it scores 100 (the only
 *  honest single-point value — there is no "relative to what" to divide by). */
function rankToValue(index: number, total: number): number {
  if (total <= 1) return 100;
  return (100 * (total - 1 - index)) / (total - 1);
}

/** Build the shared axis set from the two real orderings, axis order fixed
 *  to modelOrder's own sequence (the "default" baseline the brief's render
 *  target already treats as primary) with any lens-only achievement
 *  appended after. Never reorders, renames, or invents an axis beyond what
 *  the two served lists actually contain. */
export function buildLensComparisonAxes(
  modelOrder: string[],
  lensOrder: string[],
): LensComparisonAxis[] {
  const modelRank = new Map<string, number>();
  modelOrder.forEach((text, i) => modelRank.set(text, rankToValue(i, modelOrder.length)));
  const lensRank = new Map<string, number>();
  lensOrder.forEach((text, i) => lensRank.set(text, rankToValue(i, lensOrder.length)));

  const orderedKeys = [...modelOrder, ...lensOrder.filter((t) => !modelOrder.includes(t))];
  // De-dupe while preserving first-seen order (Set preserves insertion order).
  const uniqueKeys = [...new Set(orderedKeys)];

  return uniqueKeys.map((key) => ({
    key,
    modelValue: modelRank.get(key),
    lensValue: lensRank.get(key),
  }));
}

/** A short, non-fabricated label for an axis vertex — the full achievement
 *  text is often long-form prose, unsuitable as a radar spoke label. This
 *  truncates for DISPLAY only (the full text stays available via the
 *  existing textual list, which this chart supplements, never replaces). */
export function shortenAxisLabel(text: string, maxLen = 28): string {
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 1).trimEnd()}…`;
}
